import { useEffect, useState } from 'react';
import { ModalPortal } from '@/components/ui/Modal/ModalPortal';
import { Pencil, Loader2, AlertCircle, ToggleLeft, ToggleRight, Radio } from 'lucide-react';

export interface ChannelCatalogAdmin {
  id: number;
  slug: string;
  displayName: string;
  type: 'external' | 'internal' | string;
  featureKey?: string | null;
  isActive: boolean;
  sortOrder: number;
}

function authHeaders() {
  return {
    Authorization: `Bearer ${localStorage.getItem('admin_token')}`,
    'Content-Type': 'application/json',
  };
}

export default function CanalesCatalogPage() {
  const [channels, setChannels] = useState<ChannelCatalogAdmin[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<ChannelCatalogAdmin | null>(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState<Partial<ChannelCatalogAdmin>>({});

  const load = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await fetch('/api/super-admin/channels-catalog', {
        headers: authHeaders(),
      });
      if (!res.ok) throw new Error('Error al cargar catálogo de canales');
      const json = await res.json();
      setChannels(json.data || []);
    } catch (e: any) {
      setError(e.message || 'Error al cargar');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const openEdit = (item: ChannelCatalogAdmin) => {
    setEditing(item);
    setForm({ ...item });
  };

  const handleSave = async () => {
    if (!editing) return;
    try {
      setSaving(true);
      const res = await fetch(`/api/super-admin/channels-catalog/${editing.id}`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify({
          displayName: form.displayName,
          isActive: form.isActive,
          sortOrder: form.sortOrder,
          featureKey: form.featureKey,
        }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.message || 'Error al guardar');
      }
      setEditing(null);
      await load();
    } catch (e: any) {
      alert(e.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div
        className="flex items-center justify-center py-20 meta-text"
        role="status"
        aria-live="polite"
      >
        <Loader2 className="w-6 h-6 animate-spin mr-2 text-lyrax-primary" aria-hidden />
        Cargando canales…
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center gap-2 text-red-700 dark:text-red-300 py-10" role="alert">
        <AlertCircle className="w-5 h-5 shrink-0" aria-hidden />
        {error}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <header>
        <p className="label-caps text-lyrax-primary mb-2">Catálogo SaaS</p>
        <h1 className="page-title">Canales</h1>
        <p className="page-subtitle max-w-2xl">
          Lista global en <code className="font-mono text-lyrax-text bg-lyrax-bg px-1.5 py-0.5 rounded">public_config.channel_catalog</code>.
          Los tenants eligen de aquí; el slug no se edita.
        </p>
      </header>

      <ul className="space-y-3" aria-label="Lista de canales">
        {channels.map((ch, idx) => (
          <li key={ch.id}>
            <article
              className={`card flex items-center justify-between gap-4 animate-fade-up stagger-${Math.min(idx + 1, 4)}`}
            >
              <div className="flex items-start gap-3 min-w-0">
                <div
                  className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-lyrax bg-lyrax-primary/15 text-emerald-800 dark:text-emerald-300"
                  aria-hidden
                >
                  <Radio className="w-5 h-5" />
                </div>
                <div className="min-w-0 space-y-2">
                  <div className="flex flex-wrap items-center gap-2">
                    <h2 className="text-base font-semibold text-lyrax-text">{ch.displayName}</h2>
                    <code className="text-sm font-mono text-lyrax-text bg-lyrax-bg border border-lyrax-border px-2 py-0.5 rounded">
                      {ch.slug}
                    </code>
                    <span
                      className={
                        ch.type === 'internal' ? 'badge badge-muted' : 'badge badge-info'
                      }
                    >
                      {ch.type === 'internal' ? 'Interno' : 'Externo'}
                    </span>
                    {ch.isActive ? (
                      <span className="badge badge-success">Activo</span>
                    ) : (
                      <span className="badge badge-danger">Inactivo</span>
                    )}
                  </div>
                  <dl className="flex flex-wrap gap-x-4 gap-y-1 meta-text">
                    <div className="flex gap-1.5">
                      <dt className="font-medium text-lyrax-text">Feature</dt>
                      <dd>{ch.featureKey || '—'}</dd>
                    </div>
                    <div className="flex gap-1.5">
                      <dt className="font-medium text-lyrax-text">Orden</dt>
                      <dd>{ch.sortOrder}</dd>
                    </div>
                  </dl>
                </div>
              </div>
              <button
                type="button"
                onClick={() => openEdit(ch)}
                className="icon-btn"
                aria-label={`Editar canal ${ch.displayName}`}
              >
                <Pencil className="w-4 h-4" aria-hidden />
              </button>
            </article>
          </li>
        ))}
      </ul>

      {editing && (
        <ModalPortal>
        <div
          className="modal-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="edit-channel-title"
          onClick={(e) => {
            if (e.target === e.currentTarget) setEditing(null);
          }}
        >
          <div className="modal-panel max-w-md">
            <div className="modal-header">
              <div>
                <p className="label-caps mb-1">Canal</p>
                <h2 id="edit-channel-title" className="text-lg font-bold text-lyrax-text tracking-tight">
                  Editar {editing.displayName}
                </h2>
                <p className="meta-text mt-1 font-mono text-xs">{editing.slug}</p>
              </div>
            </div>

            <div className="modal-body">
              <div>
                <label htmlFor="channel-display-name" className="field-label">
                  Nombre visible
                </label>
                <input
                  id="channel-display-name"
                  className="field-input"
                  value={form.displayName || ''}
                  onChange={(e) => setForm((f) => ({ ...f, displayName: e.target.value }))}
                />
              </div>
              <div>
                <label htmlFor="channel-sort-order" className="field-label">
                  Orden
                </label>
                <input
                  id="channel-sort-order"
                  type="number"
                  className="field-input"
                  value={form.sortOrder ?? 0}
                  onChange={(e) => setForm((f) => ({ ...f, sortOrder: Number(e.target.value) }))}
                />
              </div>
              <button
                type="button"
                onClick={() => setForm((f) => ({ ...f, isActive: !f.isActive }))}
                className="flex items-center gap-2.5 w-full rounded-lyrax border border-lyrax-border bg-lyrax-bg px-3.5 py-3 text-sm font-medium text-lyrax-text hover:border-lyrax-primary/50 transition-colors"
                aria-pressed={!!form.isActive}
              >
                {form.isActive ? (
                  <ToggleRight className="w-6 h-6 text-emerald-700 dark:text-lyrax-primary shrink-0" aria-hidden />
                ) : (
                  <ToggleLeft className="w-6 h-6 text-lyrax-text-secondary shrink-0" aria-hidden />
                )}
                <span>{form.isActive ? 'Activo' : 'Inactivo'}</span>
              </button>
            </div>

            <div className="modal-footer">
              <button type="button" onClick={() => setEditing(null)} className="btn-secondary">
                Cancelar
              </button>
              <button
                type="button"
                disabled={saving}
                onClick={handleSave}
                className="btn-primary"
              >
                {saving ? 'Guardando…' : 'Guardar'}
              </button>
            </div>
          </div>
        </div>
        </ModalPortal>
      )}
    </div>
  );
}
